import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_SHOPIFY_STATES,
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
} from 'applications/scribe/instrumentations/track';
import { getUserCountryCode } from 'helper/user';
import { ROUTES_V2_PATHS } from 'constants/route-constants';
import { userInfoSelector } from 'selectors/app-selector';
import { isAdminSelector } from 'selectors/login-selector';
import { shopifyShopSelector } from 'selectors/shopify-selectors';
import PageHeader from 'components/v2/page-header';
import PageHeaderNav from 'components/v2/page-header-nav';
import ShopifyTrackEventContext from '../track-event';
import ShopContextProvider from '../contexts/shop-context';
import ShopBasicSettingsSection from './shop-basic-settings-section';
import ShopAddressSettingsSection from './shop-address-settings-section';
import ShopFinancialStatusSettingsSection from './shop-financial-status-settings-section';
import ShopManageEmployeeSection from './shop-manage-employee-section';
import { isPaxUser } from '../../../../helper/grab-id-auth-helper';
import styles from './shopify-settings.less';

const ShopifySettings = ({ shop, userInfo, isUserAdmin, history }) => {
  const isGfbNormalUser = !isPaxUser() && !isUserAdmin;
  const isPaxAccount = isPaxUser();

  if (!shop || isGfbNormalUser) {
    history.push(ROUTES_V2_PATHS.shopifyHome);
    return null;
  }

  const isCreateMode = !shop;

  const countryCode = getUserCountryCode(userInfo);

  const shouldShowFinancialStatusSettings = React.useMemo(
    () => countryCode === 'PH',
    [countryCode],
  );

  const fireShopifyTrackEvent = ({ stateName, eventName, facets = {} }) => {
    const shopifyCommonFacets = {
      ...getCommonFacet(userInfo),
    };
    if (shop) {
      shopifyCommonFacets[SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_NAME] = shop.name;
      shopifyCommonFacets[SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_ID] = shop.shop;
    }

    scribeTrackEvent({
      stateName,
      eventName,
      facets: {
        ...shopifyCommonFacets,
        ...facets,
      },
    });
  };

  const clickBackNavHandler = () => {
    fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_SETTINGS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.BACK_TO_SHOPIFY_ORDERS,
    });
  };

  React.useEffect(() => {
    fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_SETTINGS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.DEFAULT,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.IS_SHOPIFY_STORE_LINKED]: true,
      },
    });
  }, []);

  return (
    <ShopifyTrackEventContext.Provider
      value={{
        fireShopifyTrackEvent,
      }}
    >
      <ShopContextProvider shop={shop} userInfo={userInfo}>
        <div className={styles.ShopifySettings}>
          <PageHeader>
            <PageHeaderNav
              to={{ pathname: ROUTES_V2_PATHS.shopifyHome }}
              label={
                shop
                  ? I18n.t('shopify.settings.backButton.backToShop', {
                      shopName: shop.name,
                    })
                  : I18n.t('v2.commonButton.back')
              }
              onClick={clickBackNavHandler}
            />
            <h2>{I18n.t('shopify.settings.heading')}</h2>
          </PageHeader>
          <ShopBasicSettingsSection isCreateMode={isCreateMode} />
          {!isCreateMode && <ShopAddressSettingsSection />}
          {!isCreateMode &&
            shouldShowFinancialStatusSettings && (
              <ShopFinancialStatusSettingsSection countryCode={countryCode} />
            )}
          <ShopManageEmployeeSection isPaxAccount={isPaxAccount} />
        </div>
      </ShopContextProvider>
    </ShopifyTrackEventContext.Provider>
  );
};

ShopifySettings.propTypes = {
  shop: PropTypes.shape({}),
  userInfo: PropTypes.shape({}).isRequired,
  isUserAdmin: PropTypes.bool.isRequired,
  history: PropTypes.shape({}).isRequired,
};

ShopifySettings.defaultProps = {
  shop: null,
};

const mapStateToProps = state => ({
  shop: shopifyShopSelector(state),
  userInfo: userInfoSelector(state),
  isUserAdmin: isAdminSelector(state),
});

export default withRouter(connect(mapStateToProps)(ShopifySettings));
